import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { ExportButton } from '@/components/common/ExportButton';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { SharePanel } from '@/pages/aneel/SharePanel';
import { aneelCaseParams, fetchAneelAppeals } from '@/services/aneel';
import {
  ANEEL_OUTCOME_FILTER_ANY,
  ANEEL_OUTCOME_FILTER_NONE,
  type AneelGroupOutcomes,
  type AneelGroupTable,
  type AneelInstanceOutcomes,
  type AneelPendingDisclosure,
  type AneelStageRelief,
} from '@/services/types';

/**
 * The floor a group needs before its row is shown. Mirrors `MIN_GROUP` in
 * `analytics/aneel/appeals.py`; rendered into the help text only, the table
 * itself comes suppressed from the API.
 */
const MIN_GROUP = 20;

/**
 * Which case-list filter a group dimension opens. A nature row narrows the
 * list by `nature`, a body row by `agency`, a band row by `band` -- with
 * `first_outcome=ANY` in every case, so the list's total is the row's
 * `minuted_first` by the same predicate the table counted with.
 */
const GROUP_PARAM: Record<string, 'nature' | 'agency' | 'band'> = {
  nature: 'nature',
  body: 'agency',
  band: 'band',
};

/**
 * What the two instances do with a contested notice.
 *
 * Every share on this screen divides by the cases an instance actually ruled
 * on; the pending panel at the top prints the denominator the rest sits on and
 * the overlap between the two records of "decided" this corpus keeps. The
 * inspecting body table is alphabetical and descriptive -- who issued the
 * notice, never who ruled on it -- and no table offers a sort by outcome.
 */
export function AneelAppealsPage() {
  const { t } = useTranslation(['aneel', 'common']);

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['aneel', 'appeals'],
    queryFn: fetchAneelAppeals,
  });

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('aneel:appeals.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('aneel:appeals.subtitle')}</p>
      </header>

      <ExplainerCard i18nKey="aneel:appeals.explainer" />

      <div className="flex justify-end">
        <ExportButton path="/api/v1/aneel/export/appeals" />
      </div>

      {data ? <AsOfStrip asOf={data.as_of} /> : null}

      {isPending ? (
        <SkeletonLoader rows={8} />
      ) : error ? (
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      ) : (
        <div className="space-y-4">
          <section className="rounded-lg border bg-card p-4" data-appeals-pending>
            <h2 className="mb-3 text-sm font-semibold text-foreground">
              {t('aneel:appeals.pending.title')}
            </h2>
            <Answer answer={data.pending} help={t('aneel:appeals.pending.help')}>
              {(value) => <PendingPanel value={value} />}
            </Answer>
          </section>

          <section className="rounded-lg border bg-card p-4">
            <h2 className="mb-3 text-sm font-semibold text-foreground">
              {t('aneel:appeals.instances.title')}
            </h2>
            <div className="grid gap-3 lg:grid-cols-2">
              {data.instances.map((answer, index) => (
                <div key={answer.value?.instance ?? index} data-instance={answer.value?.instance}>
                  <Answer answer={answer} help={t('aneel:appeals.instances.help')}>
                    {(value) => <InstancePanel value={value} />}
                  </Answer>
                </div>
              ))}
            </div>
          </section>

          <section className="rounded-lg border bg-card p-4">
            <h2 className="mb-3 text-sm font-semibold text-foreground">
              {t('aneel:appeals.crossTab.title')}
            </h2>
            <Answer answer={data.cross_tab} help={t('aneel:appeals.crossTab.help')}>
              {(cells) => (
                <div className="overflow-x-auto rounded-md border">
                  <table className="w-full text-sm" data-cross-tab>
                    <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                      <tr>
                        <th className="px-3 py-2 font-medium">{t('aneel:appeals.crossTab.first')}</th>
                        <th className="px-3 py-2 font-medium">{t('aneel:appeals.crossTab.board')}</th>
                        <th className="px-3 py-2 text-right font-medium">
                          {t('aneel:appeals.crossTab.cases')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {cells.map((cell) => (
                        <tr key={`${cell.first}|${cell.board}`} className="border-t">
                          <td className="px-3 py-1.5 text-foreground">
                            <CodeList codes={cell.first} />
                          </td>
                          <td className="px-3 py-1.5 text-foreground">
                            <CodeList codes={cell.board} />
                          </td>
                          <td className="px-3 py-1.5 text-right tabular-nums text-foreground">
                            <Count value={cell.cases} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Answer>
          </section>

          {(
            [
              ['nature', data.by_nature],
              ['band', data.by_band],
              ['body', data.by_body],
            ] as const
          ).map(([kind, answer]) => (
            <section key={kind} className="rounded-lg border bg-card p-4" data-group-table={kind}>
              <h2 className="mb-3 text-sm font-semibold text-foreground">
                {t(`aneel:appeals.groups.${kind}.title`)}
              </h2>
              <Answer
                answer={answer}
                help={t(`aneel:appeals.groups.${kind}.help`, { minimum: MIN_GROUP })}
              >
                {(table) => <GroupTable table={table} />}
              </Answer>
            </section>
          ))}

          <section className="rounded-lg border bg-card p-4">
            <h2 className="mb-3 text-sm font-semibold text-foreground">
              {t('aneel:appeals.relief.title')}
            </h2>
            <div className="grid gap-3 lg:grid-cols-2">
              {data.relief.map((answer, index) => (
                <div key={answer.value?.stage ?? index} data-stage-relief={answer.value?.stage}>
                  <Answer answer={answer} help={t('aneel:appeals.relief.help')}>
                    {(value) => <ReliefPanel value={value} />}
                  </Answer>
                </div>
              ))}
            </div>
          </section>

          <FieldGuide i18nKey="aneel:appeals.guide" />
        </div>
      )}
    </div>
  );
}

function Count({ value }: { value: number }) {
  const { i18n } = useTranslation();
  return <>{new Intl.NumberFormat(i18n.language).format(value)}</>;
}

/** A `+`-joined code list, translated per code; the NONE literal reads as silence. */
function CodeList({ codes }: { codes: string }) {
  const { t } = useTranslation(['aneel']);
  if (codes === ANEEL_OUTCOME_FILTER_NONE) {
    return <span className="text-muted-foreground">{t('aneel:appeals.crossTab.none')}</span>;
  }
  return (
    <>
      {codes.split('+').map((code, index) => (
        <span key={code}>
          {index > 0 ? ' + ' : ''}
          {t(`aneel:case.outcomes.${code}`, code)}
        </span>
      ))}
    </>
  );
}

function PendingPanel({ value }: { value: AneelPendingDisclosure }) {
  const { t, i18n } = useTranslation(['aneel']);
  const number = new Intl.NumberFormat(i18n.language);
  const tiles = [
    ['pending', value.pending, { trajectory: 'PENDING_DECISION' }],
    ['resolved', value.resolved, null],
    ['minutedFirst', value.minuted_first, { first_outcome: ANEEL_OUTCOME_FILTER_ANY }],
    ['minutedBoard', value.minuted_board, { board_outcome: ANEEL_OUTCOME_FILTER_ANY }],
  ] as const;

  return (
    <div className="space-y-3">
      <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {tiles.map(([key, count, params]) => (
          <div key={key} className="rounded-md border bg-background p-3" data-pending-tile={key}>
            <dt className="text-xs text-muted-foreground">{t(`aneel:appeals.pending.${key}`)}</dt>
            <dd className="mt-1 text-lg font-semibold tabular-nums text-foreground">
              {params ? (
                <Link
                  to={`/aneel/cases?${aneelCaseParams(params).toString()}`}
                  className="text-primary underline-offset-4 hover:underline"
                >
                  {number.format(count)}
                </Link>
              ) : (
                number.format(count)
              )}
            </dd>
          </div>
        ))}
      </dl>
      <ul className="space-y-1 rounded-md border bg-muted/40 p-3 text-xs text-muted-foreground">
        <li data-pending-overlap>
          {t('aneel:appeals.pending.overlap', {
            both: number.format(value.resolved_and_minuted_first),
            resolvedOnly: number.format(value.resolved_not_minuted_first),
            minutedOnly: number.format(value.minuted_first_not_resolved),
          })}
        </li>
        {value.largest_pending_nature ? (
          <li>
            {t('aneel:appeals.pending.largest', {
              nature: value.largest_pending_nature,
              n: number.format(value.largest_pending_cases),
            })}
          </li>
        ) : null}
      </ul>
    </div>
  );
}

function InstancePanel({ value }: { value: AneelInstanceOutcomes }) {
  const { t, i18n } = useTranslation(['aneel']);
  const number = new Intl.NumberFormat(i18n.language);

  return (
    <div className="space-y-2">
      <SharePanel
        title={t(`aneel:appeals.instances.${value.instance}`)}
        shares={value.outcomes}
        total={value.minuted}
        render={(code) => t(`aneel:case.outcomes.${code}`, code)}
        multiLabelNote={(labels, cases) =>
          t('aneel:appeals.instances.multiLabel', {
            labels: number.format(labels),
            cases: number.format(cases),
          })
        }
      />
      <p className="text-xs text-muted-foreground" data-instance-coverage>
        {t('aneel:appeals.instances.coverage', {
          minuted: number.format(value.minuted),
          dated: number.format(value.dated),
          withAmount: number.format(value.with_amount),
        })}
      </p>
      {value.unknown_codes.length > 0 ? (
        <p className="text-xs text-muted-foreground" role="note">
          {t('aneel:appeals.instances.unknown', { codes: value.unknown_codes.join(', ') })}
        </p>
      ) : null}
    </div>
  );
}

function GroupTable({ table }: { table: AneelGroupTable }) {
  const { t, i18n } = useTranslation(['aneel']);
  const number = new Intl.NumberFormat(i18n.language);

  return (
    <div className="space-y-2">
      <div className="overflow-x-auto rounded-md border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
            <tr>
              <th className="px-3 py-2 font-medium">{t('aneel:appeals.groups.columns.group')}</th>
              <th className="px-3 py-2 text-right font-medium">
                {t('aneel:appeals.groups.columns.cases')}
              </th>
              <th className="px-3 py-2 text-right font-medium">
                {t('aneel:appeals.groups.columns.minutedFirst')}
              </th>
              <th className="px-3 py-2 text-right font-medium">
                {t('aneel:appeals.groups.columns.reliefFirst')}
              </th>
              <th className="px-3 py-2 text-right font-medium">
                {t('aneel:appeals.groups.columns.minutedBoard')}
              </th>
              <th className="px-3 py-2 text-right font-medium">
                {t('aneel:appeals.groups.columns.reliefBoard')}
              </th>
              <th className="px-3 py-2 font-medium">{t('aneel:appeals.groups.columns.codes')}</th>
            </tr>
          </thead>
          <tbody>
            {table.rows.map((row) => (
              <GroupRow key={row.label} kind={table.kind} row={row} />
            ))}
          </tbody>
        </table>
      </div>
      {table.suppressed_groups > 0 ? (
        <p className="text-xs text-muted-foreground" data-suppressed>
          {t('aneel:appeals.groups.suppressed', {
            groups: number.format(table.suppressed_groups),
            cases: number.format(table.suppressed_cases),
            minimum: number.format(MIN_GROUP),
          })}
        </p>
      ) : null}
    </div>
  );
}

function GroupRow({ kind, row }: { kind: string; row: AneelGroupOutcomes }) {
  const { t, i18n } = useTranslation(['aneel']);
  const number = new Intl.NumberFormat(i18n.language);
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 1,
  });
  const param = GROUP_PARAM[kind];
  const href = param
    ? `/aneel/cases?${aneelCaseParams({
        [param]: row.label,
        first_outcome: ANEEL_OUTCOME_FILTER_ANY,
      }).toString()}`
    : null;

  const codes = (shares: AneelGroupOutcomes['first']) =>
    shares
      .map((share) => `${t(`aneel:case.outcomes.${share.code}`, share.code)} ${number.format(share.count)}`)
      .join(' · ');

  return (
    <tr className="border-t align-top" data-group-row data-minuted-first={row.minuted_first}>
      <td className="px-3 py-1.5 text-foreground">
        <span className="line-clamp-2" title={row.label}>
          {row.label}
        </span>
      </td>
      <td className="px-3 py-1.5 text-right tabular-nums text-muted-foreground">
        {number.format(row.cases)}
      </td>
      <td className="px-3 py-1.5 text-right tabular-nums text-foreground">
        {href ? (
          <Link
            to={href}
            title={t('aneel:appeals.groups.openCases')}
            className="text-primary underline-offset-4 hover:underline"
            data-group-link
          >
            {number.format(row.minuted_first)}
          </Link>
        ) : (
          number.format(row.minuted_first)
        )}
      </td>
      <td className="px-3 py-1.5 text-right tabular-nums text-foreground">
        {t('aneel:appeals.groups.relief', {
          n: number.format(row.granted_first),
          share: percent.format(row.granted_first_share),
        })}
      </td>
      <td className="px-3 py-1.5 text-right tabular-nums text-foreground">
        {number.format(row.minuted_board)}
      </td>
      <td className="px-3 py-1.5 text-right tabular-nums text-foreground">
        {row.minuted_board > 0
          ? t('aneel:appeals.groups.relief', {
              n: number.format(row.granted_board),
              share: percent.format(row.granted_board_share),
            })
          : '—'}
      </td>
      <td className="px-3 py-1.5 text-xs text-muted-foreground">
        <div>
          <span className="font-medium">{t('aneel:appeals.groups.codesFirst')}</span>{' '}
          {codes(row.first)}
        </div>
        {row.board.length > 0 ? (
          <div className="mt-0.5">
            <span className="font-medium">{t('aneel:appeals.groups.codesBoard')}</span>{' '}
            {codes(row.board)}
          </div>
        ) : null}
      </td>
    </tr>
  );
}

function ReliefPanel({ value }: { value: AneelStageRelief }) {
  const { t, i18n } = useTranslation(['aneel']);
  const number = new Intl.NumberFormat(i18n.language);
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 1,
  });
  const currency = new Intl.NumberFormat(i18n.language, {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  });

  return (
    <div className="space-y-3 rounded-md border bg-background p-3">
      <h3 className="text-xs font-medium text-foreground">
        {t(`aneel:appeals.relief.${value.stage}`, value.stage)}
      </h3>
      <dl className="grid grid-cols-3 gap-3">
        {(
          [
            ['from', value.from_total],
            ['to', value.to_total],
            ['relief', value.relief],
          ] as const
        ).map(([key, amount]) => (
          <div key={key}>
            <dt className="text-xs text-muted-foreground">{t(`aneel:appeals.relief.${key}`)}</dt>
            <dd className="mt-0.5 text-sm font-semibold tabular-nums text-foreground">
              {currency.format(amount)}
            </dd>
          </div>
        ))}
      </dl>
      <p className="text-xs text-foreground" data-retained>
        {t('aneel:appeals.relief.retained', { share: percent.format(value.retained_share) })}
      </p>
      <p className="text-xs text-muted-foreground">
        {t('aneel:appeals.relief.movement', {
          reduced: number.format(value.reduced),
          same: number.format(value.same),
          increased: number.format(value.increased),
        })}
      </p>
      <p className="rounded-md border bg-muted/40 p-2 text-xs text-muted-foreground">
        {t('aneel:appeals.relief.sameCaveat')}
      </p>
    </div>
  );
}
